import { type UseApiDataOptions, useApiData } from '@repo/shared/hooks';
import { useMemo } from 'react';
import type { FareDetailsErrorResponse } from '@/api';
import {
	type FareDetailActionPopup,
	type FareDetailActionPopupResponse,
	type FareDetailSuccessResponse,
	fetchFareDetails,
} from '@/api/fareDetails';
import type { SearchFormData } from '@/components/search-form';

type FareDetailData =
	| FareDetailSuccessResponse['data']
	| FareDetailActionPopupResponse['data'];

type ErrorWithApiError = Error & { apiError?: FareDetailsErrorResponse };
function useApiDataWithCustomError<T>(
	fetcher: (options: { signal: AbortSignal }) => Promise<T>,
	dependencies: React.DependencyList,
	options: UseApiDataOptions<T> = {},
): {
	data: T | null;
	status: 'loading' | 'refreshing' | 'idle';
	error: FareDetailsErrorResponse | null;
} {
	const [{ data, status, error }] = useApiData(fetcher, dependencies, options);

	return useMemo(
		() => ({
			data,
			status,
			error: (error as ErrorWithApiError)?.apiError ?? null,
		}),
		[data, status, error],
	);
}

interface FareDetailHookOptions
	extends UseApiDataOptions<FareDetailData | null> {
	/** Accepted action types (from a prior actionPopup); re-fetches when changed */
	actions?: string[];
}

interface FareDetailHookResult {
	loading: boolean;
	error: FareDetailsErrorResponse | null;
	fareDetailsResponse: FareDetailSuccessResponse['data'];
	/** Present when the backend returns a server-driven popup instead of cabs */
	actionPopup: FareDetailActionPopup | null;
}

export function useFareDetail(
	formData: Required<SearchFormData> | null | undefined,
	options: FareDetailHookOptions = {},
): FareDetailHookResult {
	const { actions } = options;
	// Stable primitive dep so the fetcher re-runs only when the actions change
	const actionsKey = (actions ?? []).join(',');

	// Create fetcher function that useApiData will call
	// biome-ignore lint/correctness/useExhaustiveDependencies: actionsKey stands in for actions
	const fetcher = useMemo(() => {
		return async ({ signal }: { signal: AbortSignal }) => {
			if (!formData) {
				return null;
			}
			const fareDetailsResponse = await fetchFareDetails(formData, {
				actions,
				signal,
			});
			if ('errors' in fareDetailsResponse) {
				// Convert API error to Error object that useApiData expects
				const error = new Error('API Error') as ErrorWithApiError;
				error.apiError = { errors: fareDetailsResponse.errors };
				throw error;
			}

			return fareDetailsResponse.data;
		};
	}, [formData, actionsKey]);

	const { data, status, error } = useApiDataWithCustomError(
		fetcher,
		[formData, actionsKey],
		{
			...options,
			enabled: (options.enabled ?? true) && !!formData,
		},
	);

	return useMemo(() => {
		const actionPopup = data && 'actionPopup' in data ? data.actionPopup : null;
		const successData =
			data && 'tripDetails' in data
				? data
				: ({} as FareDetailSuccessResponse['data']);
		return {
			loading: status === 'loading',
			error,
			fareDetailsResponse: successData,
			actionPopup,
		};
	}, [data, status, error]);
}
